import { useRef } from "react";
import { useIonViewDidEnter, useIonViewWillEnter } from "@ionic/react";

/**
 * Workaround for Ionic leaving `ion-page-hidden` (display: none) on an IonPage
 * it is re-entering, which renders the screen blank even though the component
 * mounted and painted normally.
 *
 * It happens on screens that are navigated to with a plain react-router
 * `history.push` carrying route state — Ionic's own view stack and react-router
 * then disagree about which view is entering vs leaving. Every navigo screen
 * pushed that way is affected; the reported case was going back from
 * /providerCard to /select-location.
 *
 * Usage:
 *   const pageRef = useUnhidePageOnEnter();
 *   ...
 *   return <IonPage ref={pageRef}>...</IonPage>;
 *
 * This is a stopgap, not a fix: the underlying stack mismatch is still there.
 * Two earlier copies of the same patch already exist in
 * containers/familyInfo/newIndex.js and app/route.js — prefer this hook over a
 * fourth one, so there is a single place to delete once the root cause (likely
 * the duplicate /select-location + /select-address route registration) is
 * sorted out.
 *
 * Removing the class is safe on a healthy navigation: on the view Ionic is
 * entering the class is not set, so this is a no-op.
 */
export default function useUnhidePageOnEnter() {
  const pageRef = useRef(null);

  const unhide = () => {
    const el = pageRef.current;
    if (!el) return;
    el.classList.remove("ion-page-hidden");
  };

  // Both lifecycles, deliberately. willEnter is the normal case; didEnter is a
  // backstop for transitions where Ionic registers the view late and fires only
  // the latter (a raw history.replace is one such path — see centreFilters'
  // handleApply). Removing a class that isn't there is a no-op, so running
  // twice costs nothing.
  useIonViewWillEnter(unhide);
  useIonViewDidEnter(unhide);

  return pageRef;
}
